export type ScribeInsertAction = "copy" | "replace" | "insert" | "noop";

/**
 * Decides what to do with the resolved scribe text when the insert dialog is
 * submitted.
 *
 * Guards against an empty "Replace existing content" wiping the chart note
 * (SN-18246): when there is no resolved text (empty Summary/SOAP tab, or a
 * scribe that has not finished loading), a replace becomes a no-op instead of
 * clearing the editor. The normal insert path is unaffected.
 */
export function getScribeInsertAction(params: {
  hasEditor: boolean;
  needReplace: boolean;
  text: string;
}): ScribeInsertAction {
  const { hasEditor, needReplace, text } = params;
  if (!hasEditor) {
    return "copy";
  }
  if (!needReplace) {
    return "insert";
  }
  return text.trim() ? "replace" : "noop";
}

/**
 * Gets words around a specific position in a string
 * @param {string} text - The input text
 * @param {number} position - Character position in the text
 * @param {number} wordsBefore - Number of words to get before position (default 3)
 * @param {number} wordsAfter - Number of words to get after position (default 3)
 * @returns {Object} Object with before and after text
 */
export function getWordsAround(
  text: string,
  position: number,
  wordsBefore: number = 3,
  wordsAfter: number = 3
) {
  if (position < 0 || position > text.length) {
    return { before: "", after: "", atPosition: "" };
  }

  // Get words before position
  let beforeText = "";
  let beforeCount = 0;
  let beforePos = position;

  while (beforePos > 0 && beforeCount < wordsBefore) {
    beforePos--;
    // Check for word boundary
    if (
      text[beforePos] === " " &&
      beforePos + 1 < text.length &&
      text[beforePos + 1] !== " "
    ) {
      beforeCount++;
    }
  }
  beforeText = text.slice(beforePos, position).trim();

  // Get words after position
  let afterText = "";
  let afterCount = 0;
  let afterPos = position;

  while (afterPos < text.length && afterCount < wordsAfter) {
    afterPos++;
    // Check for word boundary
    if (
      (text[afterPos] === " " || afterPos === text.length) &&
      afterPos > 0 &&
      text[afterPos - 1] !== " "
    ) {
      afterCount++;
    }
  }
  afterText = text.slice(position, afterPos).trim();

  // Get the exact word at position
  let atPositionWord = "";
  const wordAtPosRegex = /\S+/;
  const match = text.slice(position).match(wordAtPosRegex);
  if (match) {
    atPositionWord = match[0];
  }

  return {
    before: beforeText,
    after: afterText,
    atPosition: atPositionWord
  };
}
